import { SchoolSettings } from "../types";

const PRODUCT_INVITE_USER_TYPES = new Set(["teacher"]);

interface AccessResult {
    allowed: boolean;
    reason?: string;
}

function asSet(value: string[] | undefined | null): Set<string> {
    return new Set(value ?? []);
}

/**
 * Resolve effective invite scope from plan features and optional school settings.
 * Mirrors backend get_invite_scope() including the teacher-only product gate.
 */
export function getInviteScope(
    planSendInvite: string[] | undefined,
    schoolSettings?: SchoolSettings | null,
): string[] {
    const planTypes = asSet(planSendInvite);
    const schoolRaw = schoolSettings?.send_invite;

    let scope: Set<string>;
    if (schoolRaw != null) {
        const schoolTypes = asSet(schoolRaw);
        scope = [...schoolTypes].every(t => planTypes.has(t)) ? schoolTypes : planTypes;
    } else {
        scope = planTypes;
    }

    return [...scope].filter(t => PRODUCT_INVITE_USER_TYPES.has(t)).sort();
}

/** Whether invite emails are allowed for this user type given the resolved scope. */
export function canInviteUserType(userType: string, scope: string[]): boolean {
    return userType === "teacher" && scope.includes("teacher");
}

/**
 * Check if the school settings allow sending invites to a given user type.
 * The `send_invite` array in school settings defines which user types can be invited.
 */
export function canAddUserType(
    settings: SchoolSettings | undefined,
    userType: string
): AccessResult {
    if (!settings?.send_invite) {
        return { allowed: true };
    }
    const allowed = settings.send_invite.includes(userType);
    return {
        allowed,
        reason: allowed ? undefined : `Your school settings don't allow adding ${userType}`,
    };
}

/**
 * Check if the current user's role is permitted to create students
 * based on the `student_creation` school setting.
 */
export function canCreateStudents(
    settings: SchoolSettings | undefined,
    isAdmin: boolean
): AccessResult {
    if (!settings?.student_creation) {
        return { allowed: true };
    }
    if (settings.student_creation === 'admin_only' && !isAdmin) {
        return { allowed: false, reason: 'Only admins can add students in your school' };
    }
    return { allowed: true };
}

/**
 * Check if the current user's role is permitted to share reports by email
 * based on the `result_sharing_access` school setting.
 */
export function canShareResults(
    settings: SchoolSettings | undefined,
    isAdmin: boolean,
    isTeacher: boolean,
): AccessResult {
    if (isAdmin) {
        return { allowed: true };
    }

    const sharingAccess = settings?.result_sharing_access ?? 'admin_only';

    if (sharingAccess === 'assigned_teachers_and_admin' && isTeacher) {
        return { allowed: true };
    }

    return {
        allowed: false,
        reason: sharingAccess === 'assigned_teachers_and_admin'
            ? 'Only assigned teachers and admins can share reports in your school'
            : 'Only admins can share reports in your school',
    };
}

/**
 * Check if the current user's role is permitted to create / edit
 * report templates, or to override the rubric / grade scheme / template when
 * sharing a report, based on the `report_template_access` school setting.
 *
 * Admin → always allowed. Teacher → allowed iff the setting is
 * `assigned_teachers_and_admin`. Otherwise return an explanatory reason.
 */
export function canEditReportTemplates(
    settings: SchoolSettings | undefined,
    isAdmin: boolean,
    isTeacher: boolean,
): AccessResult {
    if (isAdmin) {
        return { allowed: true };
    }

    const templateAccess = settings?.report_template_access ?? 'admin_only';

    if (templateAccess === 'assigned_teachers_and_admin' && isTeacher) {
        return { allowed: true };
    }

    return {
        allowed: false,
        reason: templateAccess === 'assigned_teachers_and_admin'
            ? 'Only assigned teachers and admins can change report templates in your school'
            : 'Only admins can change report templates in your school',
    };
}

export {
    getProfileViewAccess,
    canViewUserProfile,
} from './profileHelpers';
